import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const scrollLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Guides', href: '#guides' },
  { label: 'Categories', href: '#categories' },
  { label: 'Articles', href: '#articles' },
  { label: 'FAQ', href: '#faq' },
];

const shoppingGuideLinks = [
  { label: 'Spreadsheet Guide', to: '/basetao-spreadsheet-guide' },
  { label: 'Shipping Cost Guide', to: '/basetao-spreadsheet-guide' },
  { label: 'Warehouse Guide', to: '/basetao-spreadsheet-guide' },
  { label: 'Coupon Guide', to: '/basetao-spreadsheet-guide' },
  { label: 'Review & Safety', to: '/basetao-spreadsheet-guide' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setGuideOpen(false);
    setMobileCatsOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isHero = !scrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          {/* Logo - white on hero, dark when scrolled */}
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold transition-colors whitespace-nowrap ${
              isHero ? 'text-background-50' : 'text-foreground-900 hover:text-primary-600'
            }`}
          >
            BaseTao<span className="text-primary-400">Sheet</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {scrollLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap cursor-pointer ${
                  isHero
                    ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                {link.label}
              </button>
            ))}

            {/* Shopping Guide Dropdown */}
            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                  isHero
                    ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-50">
                  {shoppingGuideLinks.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors cursor-pointer ${
              isHero ? 'text-background-50' : 'text-foreground-700 hover:text-primary-600'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => {
            setMobileOpen(false);
            setMobileCatsOpen(false);
          }}
        />
      )}

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="fixed top-16 left-0 w-full h-[calc(100dvh-64px)] bg-background-50 border-t border-background-200/70 shadow-2xl md:hidden z-50 overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1 pb-20">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="w-full text-left px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line mr-2 text-primary-500"></i>
              Home
            </Link>
            {scrollLinks.slice(1).map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="w-full text-left px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </button>
            ))}

            {/* Mobile Shopping Guide submenu */}
            <div className="mt-3 px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider border-t border-background-200/50 pt-4">
              Shopping Guide
            </div>
            {shoppingGuideLinks.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="w-full text-left px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer pl-8"
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile Categories submenu */}
            <div className="mt-3 px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider border-t border-background-200/50 pt-4 flex items-center justify-between">
              <span>Product Categories</span>
              <button
                onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
                className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer p-1"
                aria-label="Toggle categories"
              >
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileCatsOpen ? 'rotate-180' : ''}`}></i>
              </button>
            </div>
            {mobileCatsOpen && (
              <div className="flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer pl-8"
                  >
                    <i className="ri-shopping-bag-3-line text-foreground-400 text-xs"></i>
                    {cat.name}
                    <i className="ri-external-link-line ml-auto text-xs text-foreground-400"></i>
                  </a>
                ))}
              </div>
            )}

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="mt-4 px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full text-center hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </>
  );
}